import cn from "clsx";
import { ReactNode, useId } from "react";
import { Text } from "../../text";
import { AlertIcon } from "../shared/AlertIcon";
import { AlertProps } from "../shared/AlertProps";
import styles from "./AlertBanner.module.scss";

type TitleTags = "h2";

export type AlertBannerProps = AlertProps &
    React.PropsWithChildren<{
        /**
         * Callout text to display first
         */
        title?: string;
        /**
         * Semantic element that the title should display as
         * @default h2
         */
        titleTag?: TitleTags;
        /**
         * Displays the banner with a border when true
         * @default false
         */
        showBorder?: boolean;
        /**
         * Determines the orientation of the content
         * @default "stack"
         */
        layout?: "inline" | "stack";
        /**
         * CTA to display at the end of the banner
         */
        cta?: ReactNode;
    }>;

export const AlertBanner: React.FC<AlertBannerProps> = ({
    status = "success",
    title,
    iconAltText,
    showBorder = false,
    layout = "stack",
    titleTag = "h2",
    cta,
    children,
    className = "",
    ...otherProps
}) => {
    const titleId = useId();
    return (
        <section
            aria-labelledby={title ? titleId : undefined}
            className={cn(
                styles.wrapper,
                className,
                styles[`alert--${status}`],
                {
                    [styles["alert--no-border"]]: !showBorder,
                },
            )}
            {...otherProps}
        >
            <Text.Body tag="span" className={styles.icon}>
                <AlertIcon status={status} altText={iconAltText} />
            </Text.Body>
            <div className={cn(styles.layout, styles[`layout--${layout}`])}>
                {title && (
                    <Text.Body
                        id={titleId}
                        emphasis="2"
                        tag={titleTag}
                        className={styles[`text`]}
                    >
                        {title}
                    </Text.Body>
                )}
                {children && (
                    <Text.Body
                        emphasis="1"
                        tag="div"
                        className={styles[`text`]}
                    >
                        {children}
                    </Text.Body>
                )}
                {cta && <div className={styles.cta}>{cta}</div>}
            </div>
        </section>
    );
};
